import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { DynamicPictogram } from '@/components/pictograms/DynamicPictogram'
import { PictogramName } from '@/components/pictograms/types'
import { pictogramOptions } from '@/components/pictograms/utils/PictogramOptions'
import { Label } from '@/components/shadcn/label'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { Switch } from '@/components/shadcn/switch'
import { IconV2CopyToClipboardOutline } from '@icons/IconV2CopyToClipboardOutline'
import { IconV2CorrectOutline } from '@icons/IconV2CorrectOutline'
import { motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

function PictogramCard({ pictogram, gradient }: { pictogram: PictogramName; gradient: boolean }) {
  const [isCopied, setIsCopied] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(pictogram).then(() => {
      setIsCopied(true)
      toast.success(`Copied pictogram '${pictogram}' to clipboard`)
      setTimeout(() => setIsCopied(false), 3000)
    })
  }

  return (
    <motion.div
      layout
      className="group relative flex cursor-copy flex-col items-center gap-5 rounded-lg p-5 hover:bg-variant-hover hover:text-background"
      role="button"
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCopy}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCopy()
        }
      }}
    >
      <DynamicPictogram className="size-24" pictogramName={pictogram} gradient={gradient && !isHovered} lazyLoad />
      <div className="rounded-lg bg-foreground/5 px-5 py-2 text-sm font-medium">{pictogram}</div>
      <div className="absolute right-3 top-3 ml-auto hidden group-hover:block">
        {isCopied ? <IconV2CorrectOutline size={24} /> : <IconV2CopyToClipboardOutline size={24} />}
      </div>
    </motion.div>
  )
}

export function PictogramsSection() {
  const [searchTerm, setSearchTerm] = useState('')
  const [gradient, setGradient] = useState(true)

  const filteredPictograms = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) {
      return pictogramOptions
    }

    return pictogramOptions.filter((pictogram) => pictogram.toLowerCase().includes(query))
  }, [searchTerm])

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center justify-end gap-4 border-b px-6 py-4">
        <div className="flex items-center gap-2">
          <Switch id="pictogram-gradient" checked={gradient} onCheckedChange={setGradient} />
          <Label htmlFor="pictogram-gradient" className="cursor-pointer text-sm font-medium">
            Gradient
          </Label>
        </div>
        <SearchInput value={searchTerm} onChange={setSearchTerm} />
      </div>
      <ScrollArea className="h-[min(60vh,640px)]">
        <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] px-3">
          {filteredPictograms.map((pictogram) => (
            <PictogramCard key={pictogram} pictogram={pictogram} gradient={gradient} />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
